import apiV2Service from '../api/api-v2.service';
import {ApiOptions, Payload} from '../api/apiTypes';
import {
  ContactUsEmailParams,
  PasswordValidationRules,
  SignOutResponse,
  SSOUrlResponse,
  UserAuthBaseInfo,
  UserAuthInfo,
} from './auth.types';

const BASE_PATH = 'Authentication';

async function signIn(email: string, password: string): Promise<UserAuthInfo> {
  const {data} = await apiV2Service.executePostRequest<UserAuthInfo>(
    `${BASE_PATH}/SignIn`,
    {},
    {email, password},
    {includeClientId: false},
  );

  return data;
}

async function validatePassword(
  newPassword: string,
  email: string,
): Promise<PasswordValidationRules> {
  const {data} = await apiV2Service.executePostRequest<PasswordValidationRules>(
    `${BASE_PATH}/ValidatePassword`,
    {},
    {newPassword, email},
    {includeClientId: false},
  );

  return data;
}

async function validatePasswordForNewUser({
  password,
  lastName,
  firstName,
  registrationToken,
}: {
  password: string;
  registrationToken: string;
  firstName: string;
  lastName: string;
}): Promise<PasswordValidationRules> {
  const options: ApiOptions = {
    skipAuth: true,
    includeClientId: false,
    headers: {Authorization: `Bearer ${registrationToken}`},
  };

  const {data} = await apiV2Service.executePostRequest<PasswordValidationRules>(
    `${BASE_PATH}/ValidatePasswordForNewUser`,
    {},
    {password, lastName, firstName},
    options,
  );

  return data;
}

async function verifyMultiFactorCode(email: string, mfaCode: string): Promise<UserAuthInfo> {
  const {data} = await apiV2Service.executePostRequest<UserAuthInfo>(
    `${BASE_PATH}/VerifyMultiFactorCode`,
    {},
    {email, mfaCode},
    {includeClientId: false},
  );

  return data;
}

function forgotPasswordSendEmail(email: string): Promise<Payload<void>> {
  return apiV2Service.executePostRequest<void>(
    `${BASE_PATH}/ForgotPasswordSendEmail`,
    {},
    {email},
    {
      skipAuth: true,
      includeClientId: false,
    },
  );
}

async function logout() {
  const {
    data: {singleSignOutRedirectUrl},
  } = await apiV2Service.executePostRequest<SignOutResponse>(
    `${BASE_PATH}/SignOut`,
    {},
    {},
    {includeClientId: false},
  );

  return singleSignOutRedirectUrl;
}

function contactUsEmail(params: ContactUsEmailParams) {
  return apiV2Service.executePostRequest<void>(
    `${BASE_PATH}/ContactUsEmail`,
    {},
    {...params},
    {
      skipAuth: true,
      includeClientId: false,
    },
  );
}

async function changePassword({
  oldPassword,
  newPassword,
  email,
}: {
  oldPassword: string;
  newPassword: string;
  email: string;
}): Promise<UserAuthBaseInfo> {
  const paramsObj = {
    oldPassword,
    newPassword,
    email,
  };

  const {
    data: {token, clientId},
  } = await apiV2Service.executePostRequest<UserAuthBaseInfo>(
    `${BASE_PATH}/ChangePassword`,
    {},
    paramsObj,
    {includeClientId: false},
  );

  return {token, clientId};
}

async function changePasswordByResetToken({
  password,
  passwordResetToken,
}: {
  password: string;
  passwordResetToken: string;
}): Promise<unknown> {
  const paramsObj = {
    password,
    passwordResetToken,
  };

  return apiV2Service.executePostRequest(`${BASE_PATH}/ChangePasswordByResetToken`, {}, paramsObj, {
    includeClientId: false,
  });
}

async function getSsoUrl(userEmail: string): Promise<string> {
  const {
    data: {ssoUrl},
  } = await apiV2Service.executeGetRequest<SSOUrlResponse>(
    `${BASE_PATH}/SignInMethod/${userEmail}`,
    {},
    {
      skipAuth: true,
      includeClientId: false,
    },
  );

  return ssoUrl;
}

const authApi = {
  signIn,
  forgotPasswordSendEmail,
  contactUsEmail,
  changePassword,
  validatePassword,
  verifyMultiFactorCode,
  logout,
  changePasswordByResetToken,
  validatePasswordForNewUser,
  getSsoUrl,
};

export default authApi;
